import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { chooseBillingCompany, downloadInvoicePdf, readBilling } from '../../api/business/endpoints';
import type { InvoiceStatus, InvoiceSummary } from '../../api/shared';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate, formatMoney } from '../../components/format';
import { InvoiceStatusChip } from '../../components/InvoiceStatusChip';
import { PageHeader } from '../../components/PageHeader';
import { saveBlob } from '../../components/saveBlob';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

type InvoiceSortKey = 'number' | 'issuedOn' | 'dueOn' | 'totalMinor' | 'status';

const VALUE_OF: Record<InvoiceSortKey, (invoice: InvoiceSummary) => string | number | null> = {
  number: (invoice) => invoice.number,
  issuedOn: (invoice) => invoice.issuedOn,
  dueOn: (invoice) => invoice.dueOn,
  totalMinor: (invoice) => invoice.totalMinor,
  status: (invoice) => invoice.status,
};

const STATUSES: Array<InvoiceStatus> = ['ISSUED', 'PARTIALLY_PAID', 'PAID', 'CANCELLED'];

/**
 * The business's licence documents, and the company they are addressed to (R0006 Q-04, D-12). The whole
 * list is held, so it sorts in the browser (Conventions/08 §3).
 */
export const BillingPage = () => {
  const { language, t } = useLanguage();
  const permissions = usePermissions();
  const query = useQuery({ queryKey: ['billing'], queryFn: readBilling });
  const [companyId, setCompanyId] = useState('');
  const [saved, setSaved] = useState(false);
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const sort = useSort<InvoiceSortKey>('issuedOn', 'desc');
  const choose = useAppMutation(
    (id: string) => chooseBillingCompany(id),
    ['billing', 'audit'],
    () => setSaved(true),
  );

  useEffect(() => {
    if (query.data) {
      setCompanyId(query.data.billingCompanyId ?? '');
    }
  }, [query.data]);

  const invoices = query.data?.invoices ?? [];
  const awaiting = invoices.find(
    (invoice) =>
      invoice.documentType === 'PROFORMA' &&
      (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_PAID'),
  );
  const needle = term.trim().toLowerCase();
  const rows = sortRows(
    invoices.filter(
      (invoice) =>
        (status === '' || invoice.status === status) &&
        (needle === '' ||
          invoice.number.toLowerCase().includes(needle) ||
          invoice.buyerName.toLowerCase().includes(needle)),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );

  const download = async (invoice: InvoiceSummary): Promise<void> => {
    setDownloadError(null);

    try {
      saveBlob(
        await downloadInvoicePdf(invoice.id),
        `${invoice.documentType === 'PROFORMA' ? 'zalohova-faktura' : 'faktura'}-${invoice.number}.pdf`,
      );
    } catch (caught) {
      setDownloadError(caught);
    }
  };

  return (
    <>
      <PageHeader title={t('billing.title')} subtitle={t('billing.subtitle')} />
      <ErrorNotice error={query.error ?? downloadError} onRetry={() => void query.refetch()} />
      {awaiting ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
          {t('billing.awaiting', {
            number: awaiting.number,
            total: formatMoney(awaiting.totalMinor - awaiting.paidMinor, awaiting.currency, language),
            due: formatDate(awaiting.dueOn, language),
          })}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6">{t('billing.company')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('billing.companyHint')}
        </Typography>
        {query.isLoading ? (
          <Skeleton variant="rounded" height={56} />
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              select
              label={t('field.company')}
              value={companyId}
              disabled={!permissions.can('billing.manage')}
              onChange={(event) => {
                setSaved(false);
                setCompanyId(event.target.value);
              }}
              sx={{ minWidth: 320 }}
            >
              {(query.data?.companies ?? []).map((company) => (
                <MenuItem
                  key={company.id}
                  value={company.id}
                  disabled={company.status !== 'ACTIVE' || !company.billable}
                >
                  {company.name}
                  {company.billable ? '' : ` — ${t('tenants.notBillable')}`}
                </MenuItem>
              ))}
            </TextField>
            {permissions.can('billing.manage') ? (
              <Button
                variant="contained"
                disabled={choose.isPending || companyId === '' || companyId === query.data?.billingCompanyId}
                onClick={() => choose.mutate(companyId)}
              >
                {t('common.save')}
              </Button>
            ) : null}
            {saved ? (
              <Typography variant="body2" color="success.main">
                {t('billing.saved')}
              </Typography>
            ) : null}
          </Stack>
        )}
        <Box sx={{ mt: 2 }}>
          <ErrorNotice error={choose.error} />
        </Box>
      </Paper>

      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('invoices.searchHint')}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
          <TextField
            select
            size="small"
            label={t('field.status')}
            value={status}
            onChange={(event) => setStatus(event.target.value as InvoiceStatus | '')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`invoiceStatus.${value}` as const)}
              </MenuItem>
            ))}
          </TextField>
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="number">
                  {t('field.number')}
                </SortableCell>
                <TableCell>{t('field.documentType')}</TableCell>
                <SortableCell sort={sort} column="issuedOn">
                  {t('field.issuedOn')}
                </SortableCell>
                <SortableCell sort={sort} column="dueOn">
                  {t('field.dueOn')}
                </SortableCell>
                <SortableCell sort={sort} column="totalMinor" align="right">
                  {t('field.total')}
                </SortableCell>
                <SortableCell sort={sort} column="status">
                  {t('field.status')}
                </SortableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading ? (
                <TableSkeletonRows columns={7} />
              ) : (
                rows.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell sx={{ fontWeight: 560 }}>{invoice.number}</TableCell>
                    <TableCell>{t(`documentType.${invoice.documentType}` as const)}</TableCell>
                    <TableCell>{formatDate(invoice.issuedOn, language)}</TableCell>
                    <TableCell>{formatDate(invoice.dueOn, language)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(invoice.totalMinor, invoice.currency, language)}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusChip status={invoice.status} overdue={invoice.overdue} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => void download(invoice)}
                      >
                        {t('invoice.download')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && rows.length === 0 ? (
          <EmptyState title={t('billing.empty')} detail={t('billing.emptyDetail')} />
        ) : null}
      </Paper>
    </>
  );
};
